import {
  type ColumnDef,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { SortingState } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { useEmployeeResourceGroupInteractionsQuery } from "./EmployeeResourceGroupsTable.g";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";
import { Tag } from "@design-system/Tag/Tag";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type ResourceGroupData = {
  id: string;
  primaryApplicationId: string;
  productName: string;
  vendorName: string;
  productDomainName: string;
  description: string;
  category: string;
  signedLogoUrl: string | null;
  lastUsed: Date | null;
  totalApplications: number;
};

const columnHelper = createColumnHelper<ResourceGroupData>();

type Props = {
  clientId: string;
  clientEmployeeId: string;
  initialPageIndex?: number;
  search?: string;
  pageSize?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  dateRange?: [Date | null, Date | null];
  parentCategoryId?: string;
};

export const EmployeeResourceGroupsTable = ({
  clientId,
  clientEmployeeId,
  initialPageIndex = 0,
  search,
  pageSize = PAGE_SIZE,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  dateRange,
  parentCategoryId,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { columnSettings } = useTableColumnSettingsProvider();

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);

  // Generate sort parameter for backend
  // Valid sort fields: 'name', 'last_interaction', 'total_interactions'
  const sortBy = useMemo(() => {
    if (sorting.length === 0) return undefined;

    const sortConfig = sorting[0];
    const sortFieldMap: Record<string, string> = {
      productName: "name",
      vendorName: "name",
      description: "name",
      lastUsed: "last_interaction",
    };

    const field = sortFieldMap[sortConfig.id];
    if (!field) return undefined;

    return sortConfig.desc ? `-${field}` : field;
  }, [sorting]);

  // Convert date range to Date strings (YYYY-MM-DD format)
  const dateFrom = dateRange?.[0]
    ? dayjs(dateRange[0]).format("YYYY-MM-DD")
    : undefined;
  const dateTo = dateRange?.[1]
    ? dayjs(dateRange[1]).format("YYYY-MM-DD")
    : undefined;

  // Fetch employee application interactions with server-side pagination
  const interactionsQuery = useEmployeeResourceGroupInteractionsQuery(
    {
      employeeId: clientEmployeeId,
      dateFrom,
      dateTo,
      ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
      orderBy: sortBy,
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isLoading = interactionsQuery.isLoading;
  const isFetching = interactionsQuery.isFetching;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pageSize });
  }, [debouncedSearch, pageSize, dateRange, parentCategoryId, sortBy]);

  // Process data - server handles pagination, sorting, and filtering
  const { data, totalCount } = useMemo(() => {
    const result = interactionsQuery.data?.clientEmployeeApplicationInteractions;
    const interactions = result?.interactions;

    if (!interactions || interactions.length === 0) {
      return { data: [], totalCount: 0 };
    }

    // Map interactions to table data format
    const mappedData: ResourceGroupData[] = interactions.map((interaction) => {
      const primaryResource = interaction.primaryResource;
      const publicResource = primaryResource.publicResource;
      const category = publicResource?.category;

      return {
        id: interaction.resourceGroup.id,
        primaryApplicationId: interaction.application.id,
        productName: primaryResource.productName || "",
        vendorName: primaryResource.vendorName || "",
        productDomainName: primaryResource.productDomainName || "",
        description: primaryResource.productDescription || "",
        category:
          category?.parent?.name || category?.name || "",
        signedLogoUrl: publicResource?.signedLogoUrl || null,
        lastUsed: interaction.lastInteractionAt
          ? new Date(interaction.lastInteractionAt)
          : null,
        totalApplications:
          1 + (interaction.relatedApplications?.length || 0),
      };
    });

    // Apply client-side search filter (not handled by backend yet)
    let filteredData = mappedData;
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filteredData = mappedData.filter(
        (item) =>
          item.productName.toLowerCase().includes(searchLower) ||
          item.vendorName.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower)
      );
    }

    // Use filteredCount from server for accurate pagination
    // If client-side search is active, use filtered data length
    const count = debouncedSearch ? filteredData.length : (result?.filteredCount || 0);

    return {
      data: filteredData,
      totalCount: count,
    };
  }, [interactionsQuery.data, debouncedSearch]);

  const handlePrimaryApplicationClick = useCallback(
    (applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.dashboard.$buildPath(
          {
            params: { clientId, applicationId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("productName", {
        header: "Application",
        id: "productName",
        cell: (props) => (
          <Clickable
            onClick={() => handlePrimaryApplicationClick(props.row.original.primaryApplicationId)}
            outline="sm"
            stretch
          >
            <Table.CompanyCell
              name={props.getValue()}
              subtext={props.row.original.vendorName || undefined}
              signedLogoUrl={props.row.original.signedLogoUrl || undefined}
              domain={props.row.original.productDomainName || ""}
            />
          </Clickable>
        ),
      }),
      columnHelper.accessor("description", {
        header: "Description",
        id: "description",
        cell: (props) => (
          <Text truncate maxWidth={200}>
            {props.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("category", {
        header: "Category",
        id: "category",
        enableSorting: false,
        cell: (props) => {
          const category = props.getValue();
          return category ? (
            <Box>
              <Tag color="secondary" text={category} />
            </Box>
          ) : (
            <Text>-</Text>
          );
        },
      }),
      columnHelper.accessor("lastUsed", {
        header: "Last Used",
        id: "lastUsed",
        cell: (props) => {
          const date = props.getValue();
          return date ? (
            <Box>
              <Table.DateCell date={date} dayOnly />
            </Box>
          ) : (
            <Text>-</Text>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => (
          <Box gap={2}>
            <Dropdown
              trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
            >
              <Dropdown.Option
                label="Dashboard"
                icon="faGrip"
                onSelect={() =>
                  handlePrimaryApplicationClick(props.row.original.primaryApplicationId)
                }
              />
              <Dropdown.Option
                label="Record"
                icon="faFileInvoice"
                onSelect={() =>
                  navigate(
                    routes.portal.clients.client.applications.application.record.$buildPath(
                      {
                        params: {
                          clientId,
                          applicationId: props.row.original.primaryApplicationId,
                        },
                      },
                    )
                  )
                }
              />
              <Dropdown.Option
                label="Users"
                icon="faUser"
                onSelect={() =>
                  navigate(
                    routes.portal.clients.client.applications.application.users.$buildPath(
                      {
                        params: {
                          clientId,
                          applicationId: props.row.original.primaryApplicationId,
                        },
                      },
                    )
                  )
                }
              />
            </Dropdown>
          </Box>
        ),
      }),
    ] as ColumnDef<ResourceGroupData, unknown>[],
    [handlePrimaryApplicationClick, clientId, navigate]
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table<ResourceGroupData, string>
      columns={columns as ColumnDef<ResourceGroupData, string>[]}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={isLoading}
      isFetching={isFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
      manualSorting={true}
      showFooter={showFooter}
      showHeader={showHeader}
      showPageSizeDropdown={showPageSizeDropdown}
      columnVisibility={columnSettings}
    />
  );
};